import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { analyticsAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuth';

export default function Analytics() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalytics();
    loadDuplicates();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await analyticsAPI.getAnalytics();
      setAnalytics(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadDuplicates = async () => {
    try {
      const response = await analyticsAPI.getDuplicates();
      setDuplicates(response.data.duplicates || []);
    } catch (err) {
      console.error('Failed to load duplicates:', err);
    }
  };

  const handleExport = () => {
    window.open('/api/analytics/export', '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Analytics</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const { summary, statusDistribution, monthlyTrends, cycleTimes, bottlenecks, userActivity } = analytics || {};

  // Format an hours value (may be a numeric string from PG) as "Xh" or "Yd Zh"
  const formatDuration = (hours) => {
    const h = Number(hours);
    if (!Number.isFinite(h) || h <= 0) return '—';
    if (h < 48) return `${Math.round(h)}h`;
    const days = Math.floor(h / 24);
    const rem = Math.round(h % 24);
    return rem ? `${days}d ${rem}h` : `${days}d`;
  };

  const statusLabel = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-primary-600 hover:text-primary-800">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
                <p className="text-sm text-gray-600">Submission metrics and insights</p>
              </div>
            </div>
            <button onClick={handleExport} className="btn btn-primary">
              <svg className="h-5 w-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="card">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Total Submissions</h3>
              <p className="text-3xl font-bold text-gray-900 tabular-nums">{Number(summary.total_submissions).toLocaleString()}</p>
            </div>
            <div className="card">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Last 7 Days</h3>
              <p className="text-3xl font-bold text-primary-600 tabular-nums">{Number(summary.last_7_days).toLocaleString()}</p>
            </div>
            <div className="card">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Last 30 Days</h3>
              <p className="text-3xl font-bold text-primary-600 tabular-nums">{Number(summary.last_30_days).toLocaleString()}</p>
            </div>
            <div className="card">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Approval Rate</h3>
              <p className="text-3xl font-bold text-green-600 tabular-nums">{summary.approval_rate}%</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Status Distribution */}
          {statusDistribution && statusDistribution.length > 0 && (
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Status Distribution</h2>
              <div className="space-y-3">
                {statusDistribution.map((item) => {
                  const total = Number(summary?.total_submissions) || 0;
                  const count = Number(item.count) || 0;
                  const pct = total > 0 ? (count / total) * 100 : 0;
                  return (
                    <div key={item.status} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {item.status.replace(/_/g, ' ')}
                      </span>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-400 tabular-nums w-10 text-right mr-2">
                          {pct >= 1 ? `${Math.round(pct)}%` : ''}
                        </span>
                        <div className="w-32 h-2.5 bg-gray-100 rounded-full mr-3 overflow-hidden">
                          <div
                            className="h-full bg-primary-500 rounded-full"
                            style={{ width: `${pct}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-bold text-gray-900 tabular-nums w-10 text-right">{count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cycle Times (real per-stage durations) */}
          {cycleTimes && (
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Cycle Times</h2>
              <p className="text-xs text-gray-500 mb-4">Median and average time submissions spend in each stage.</p>
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Intake → Submitted for review</p>
                    <p className="text-xs text-gray-500">
                      {cycleTimes.intakeToSubmit?.n ? `${cycleTimes.intakeToSubmit.n} submissions` : 'No data yet'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">{formatDuration(cycleTimes.intakeToSubmit?.median_hours)}</p>
                    <p className="text-xs text-gray-500">avg {formatDuration(cycleTimes.intakeToSubmit?.avg_hours)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Submitted → Carrier decision</p>
                    <p className="text-xs text-gray-500">
                      {cycleTimes.submitToDecision?.n ? `${cycleTimes.submitToDecision.n} submissions` : 'No data yet'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">{formatDuration(cycleTimes.submitToDecision?.median_hours)}</p>
                    <p className="text-xs text-gray-500">avg {formatDuration(cycleTimes.submitToDecision?.avg_hours)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Where submissions are waiting (bottleneck / aging snapshot) */}
        {bottlenecks && bottlenecks.length > 0 && (
          <div className="card mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Where Submissions Are Waiting</h2>
            <p className="text-xs text-gray-500 mb-4">
              Open submissions by current stage, with how long they have been sitting there. Items in the 7+ day column need attention.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Open</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Avg age</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">&lt; 3d</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">3–7d</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">7+ d</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bottlenecks.map((b) => (
                    <tr key={b.status} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{statusLabel(b.status)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right tabular-nums">{b.count}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right tabular-nums">
                        {Number(b.avg_age_days).toFixed(1)}d
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right tabular-nums">{b.bucket_lt_3d}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right tabular-nums">{b.bucket_3_7d}</td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums font-semibold ${Number(b.bucket_gt_7d) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {b.bucket_gt_7d}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Monthly Trends */}
        {monthlyTrends && monthlyTrends.length > 0 && (() => {
          const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const counts = monthlyTrends.map((t) => parseInt(t.count, 10) || 0);
          const maxCount = Math.max(...counts, 1);
          return (
            <div className="card mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Monthly Submissions</h2>
              <p className="text-xs text-gray-500 mb-6">New submissions received per month, last 12 months.</p>
              <div className="overflow-x-auto">
                <div className="min-w-[560px]">
                  {/* Plot: bars grow from a shared baseline */}
                  <div className="flex items-end gap-2 h-52 border-b border-gray-200">
                    {monthlyTrends.map((item, i) => {
                      const count = counts[i];
                      // Cap the tallest bar at 90% so the value label has headroom;
                      // give any non-zero month at least a 4% sliver so it reads.
                      const barHeight = count > 0 ? Math.max((count / maxCount) * 90, 4) : 0;
                      const [year, month] = item.month.split('-');
                      const label = `${MONTHS[parseInt(month, 10) - 1]} ${year}`;
                      return (
                        <div
                          key={item.month}
                          className="group flex-1 flex flex-col items-center justify-end h-full"
                          title={`${label} — ${count} submission${count === 1 ? '' : 's'}`}
                        >
                          <span className="text-[11px] font-semibold text-gray-500 tabular-nums mb-1 h-4">
                            {count > 0 ? count : ''}
                          </span>
                          <div
                            className="w-full max-w-[44px] rounded-t bg-primary-500 group-hover:bg-primary-600 transition-colors"
                            style={{ height: `${barHeight}%` }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  {/* Axis: horizontal month labels; year shown only when it changes */}
                  <div className="flex gap-2 mt-2">
                    {monthlyTrends.map((item, i) => {
                      const [year, month] = item.month.split('-');
                      const prevYear = i > 0 ? monthlyTrends[i - 1].month.split('-')[0] : null;
                      const showYear = i === 0 || year !== prevYear;
                      return (
                        <div key={item.month} className="flex-1 text-center">
                          <div className="text-xs text-gray-600">{MONTHS[parseInt(month, 10) - 1]}</div>
                          <div className="text-[10px] text-gray-400 h-3">{showYear ? year : ''}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Duplicate Phone Numbers */}
        {duplicates && duplicates.length > 0 && (
          <div className="card mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              <svg className="h-5 w-5 inline text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Duplicate Phone Numbers ({duplicates.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submission IDs</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {duplicates.map((dup) => (
                    <tr key={dup.phone_number} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {dup.phone_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {dup.count} duplicates
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {dup.submissions.map((sub, i) => (
                          <span key={sub.id}>
                            {i > 0 && ', '}
                            <Link to={`/submissions/${sub.id}`} className="text-primary-600 hover:text-primary-800">
                              {sub.id}
                            </Link>
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* User Activity */}
        {userActivity && userActivity.length > 0 && (
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Top Submitters to Twilio</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submissions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {userActivity.map((user) => (
                    <tr key={user.user_email} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {user.user_email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.submissions_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
